import {createSelector} from 'reselect';
import {riskTypes, AccountType, documentTypes, estimateTypes} from '../../util/uiconstants';
import {
	findRelatedALRARelatedDocumentIdsForAccount,
	shouldShowOtherInherentRiskFactorColumns,
	getRelatedDocId
} from '../../components/Common/CanvasForm/Bodies/BodyTypes/BodySpecial/ALRASummaryBody/accountSummaryUtilityFunctions';
import Utility from '../../util/utilityfunctions';

export const getAccountsSlice = (state) => state.accounts;
export const getAssertionsSlice = (state) => state._assertions;
export const getRisksSlice = (state) => state._risks;

export const getAccountsForALRA = createSelector([getAccountsSlice], (slice) => {
	let result = [];
	if (slice.allIds && slice.byId) {
		result = slice.allIds.map((accountId) => slice.byId[accountId]);
	}
	return _.orderBy(result, [(account) => account.accountName.toLowerCase()], ['asc']);
});

export const getAccountsForALRAAGGridTreeView = (state) => {
	const slice = getAccountsSlice(state);

	let accounts = [];
	if (!Array.isArray(slice)) {
		accounts = Object.values(slice?.byId);
	}

	const riskTypeDisplayOrder = [riskTypes.SIGNIFICANTRISK, riskTypes.FRAUDRISK, riskTypes.INHERENTRISK];

	//Iterate accounts to create all parent and child rows for tree grid
	let rawAccounts = [];
	for (const ac of accounts) {
		//Create parent row
		let headerAc = _.cloneDeep(ac);
		headerAc.parent = true;
		headerAc.path = [];
		headerAc.path.push(`${ac.id}|${ac.accountName}`);
		rawAccounts.push(headerAc);

		//Check for limited risk and insignificant risk account types
		//Create corresponding child row and continue
		if (ac.accountTypeId === AccountType.LimitedRisk || ac.accountTypeId === AccountType.InSignificant) {
			let childValues = {
				[AccountType.LimitedRisk]: 'LIMITED_RISK_ACCOUNT',
				[AccountType.InSignificant]: 'INSIGNIFICANT_RISK_ACCOUNT'
			};
			let childAc = _.cloneDeep(ac);
			childAc.path = [];
			childAc.path.push(`${ac.id}|${ac.accountName}`);
			childAc.path.push(childValues[ac.accountTypeId]);
			childAc.accountTypeId = null;
			childAc.fullWidthRow = true;
			rawAccounts.push(childAc);
			continue;
		}

		//Process account different than limited risk and insignificant risk account type
		//Create child rows if account has risks through assertions
		let accountRisks = [];
		if (ac.assertions && ac.assertions.length > 0) {
			ac.assertions.map((assertion) => {
				if (assertion && assertion.risks && assertion.risks.length > 0) {
					assertion.risks?.map((risk) => {
						if (risk.riskTypeId <= 3) {
							let risksChildAc = _.cloneDeep(ac);
							risksChildAc.path = [];
							risksChildAc.path.push(`${ac.id}|${ac.accountName}`);
							risksChildAc.path.push(`${risk.id}|${risk.riskName}`);
							risksChildAc.accountTypeId = null;
							risksChildAc.fullWidthRow = true;
							risksChildAc.assertion = assertion;
							risksChildAc.risk = risk;

							accountRisks.push(risksChildAc);
						}
					});
				}
			});
		}

		//Sort account risks
		accountRisks.sort((a, b) => {
			return (
				riskTypeDisplayOrder.indexOf(a?.risk?.riskTypeId) - riskTypeDisplayOrder.indexOf(b?.risk?.riskTypeId) ||
				a.risk?.riskName.localeCompare(b.risk?.riskName)
			);
		});

		rawAccounts = [...rawAccounts, ...accountRisks];

		//Create corresponding child row if account does not have risks through assertions
		if (accountRisks.length === 0) {
			const noRisksChildAc = _.cloneDeep(ac);
			noRisksChildAc.path = [];
			noRisksChildAc.path.push(`${ac.id}|${ac.accountName}`);
			noRisksChildAc.path.push('NO_RISKS_ASSOCIATED');
			noRisksChildAc.accountTypeId = null;
			noRisksChildAc.fullWidthRow = true;
			rawAccounts.push(noRisksChildAc);
		}
	}

	//Create unique accounts by risk and add its corresponding assertions
	let accountsWithoutRisks = rawAccounts.filter((a) => !a.assertion || !a.risk);
	let accountsWithRisks = rawAccounts.filter((a) => a.assertion && a.risk);

	let uniqueAccountsWithRisks = _.uniqBy(accountsWithRisks, (uac) => [uac.id, uac.risk.id].join());

	uniqueAccountsWithRisks.forEach((ac) => {
		let riskAccounts = accountsWithRisks.filter((uac) => uac.id === ac.id && uac.risk.id === ac.risk.id);
		ac.assertionsRisks = [];
		if (riskAccounts && riskAccounts.length > 0) {
			for (let i = 0; i < riskAccounts.length; i++) {
				const riskAccount = riskAccounts[i];
				ac.assertionsRisks.push({assertion: riskAccount.assertion, risk: riskAccount.risk});
			}
		}
	});

	const uniqueAccounts = accountsWithoutRisks.concat(uniqueAccountsWithRisks);

	//Sort accounts by name and return
	return _.orderBy(uniqueAccounts, [(account) => account.accountName.toLowerCase()], ['asc']);
};

export const getAccountsForInherentRiskFactors = (state, formActions) => {
	const slice = getAccountsSlice(state);
	const assertions = getAssertionsSlice(state)?.byId;
	const risks = getRisksSlice(state)?.byId;

	let accounts = [];
	if (!Array.isArray(slice)) {
		accounts = Object.values(slice?.byId);
	}

	const riskTypeDisplayOrder = [riskTypes.SIGNIFICANTRISK, riskTypes.FRAUDRISK, riskTypes.INHERENTRISK];

	//Iterate accounts to create all parent and child rows for tree grid
	let rawAccounts = [];
	for (const ac of accounts) {
		/*get other fields from ac*/
		const shouldShowOtherColumns = shouldShowOtherInherentRiskFactorColumns(ac, formActions);
		const relatedDocIds = findRelatedALRARelatedDocumentIdsForAccount(ac);
		const ALRARelatedDoc = ac.relatedDocuments.find((rd) => rd.documentTypeID === documentTypes.ALRA_INDIVIDUAL);

		//Create parent row
		let headerAc = _.cloneDeep(ac);
		headerAc.parent = true;
		headerAc.path = [];
		headerAc.path.push(`${ac.id}|${ac.accountName}`);
		headerAc.shouldShowOtherColumns = shouldShowOtherColumns;
		headerAc.higher = true;
		headerAc.relatedDocId = getRelatedDocId(relatedDocIds, ALRARelatedDoc);
		rawAccounts.push(headerAc);

		//Check for limited risk and insignificant risk account types
		//Create corresponding child row and continue
		if (ac.accountTypeId === AccountType.LimitedRisk || ac.accountTypeId === AccountType.InSignificant) {
			let childValues = {
				[AccountType.LimitedRisk]: 'LIMITED_RISK_ACCOUNT',
				[AccountType.InSignificant]: 'INSIGNIFICANT_RISK_ACCOUNT'
			};
			let childAc = _.cloneDeep(ac);
			childAc.path = [];
			childAc.path.push(`${ac.id}|${ac.accountName}`);
			childAc.path.push(childValues[ac.accountTypeId]);
			childAc.accountTypeId = null;
			rawAccounts.push(childAc);
			continue;
		}

		//Process account different from limited risk and insignificant risk account type
		//Create child rows if account has assertions
		let accountAssertions = [];
		let accountRisks = [];
		if (ac.assertions && ac.assertions.length > 0) {
			const sortedAssertions = ac.assertions.sort(Utility.sortByMultipleKey('knowledgeAssertionId'));
			sortedAssertions.map((as) => {
				const assertion = assertions[as.id];
				if (assertion) {
					let assertionChildAc = _.cloneDeep(ac);
					assertionChildAc.path = [];
					assertionChildAc.path.push(`${ac.id}|${ac.accountName}`);
					assertionChildAc.path.push(`${as.knowledgeAssertionId}`);
					assertionChildAc.assertion = as;
					assertionChildAc.lower = true;
					assertionChildAc.shouldShowOtherColumns = shouldShowOtherColumns;
					assertionChildAc.relatedDocId = getRelatedDocId(relatedDocIds, ALRARelatedDoc);
					accountAssertions.push(assertionChildAc);

					if (assertion && assertion.risks && assertion.risks.length > 0) {
						assertion.risks?.map((r) => {
							const risk = risks[r];
							if (risk.riskTypeId <= 3) {
								let risksChildAc = _.cloneDeep(ac);
								risksChildAc.path = [];
								risksChildAc.path.push(`${ac.id}|${ac.accountName}`);
								risksChildAc.path.push(`${as.knowledgeAssertionId}`);
								risksChildAc.path.push(`${risk.id}|${risk.riskName}`);
								risksChildAc.assertion = as;
								risksChildAc.shouldShowOtherColumns = shouldShowOtherColumns;
								risksChildAc.relatedDocId = getRelatedDocId(relatedDocIds, ALRARelatedDoc);
								risksChildAc.risk = risk;
								accountRisks.push(risksChildAc);
							}
						});
					} else {
						/*create empty risk row*/
						const noRisksChildAc = _.cloneDeep(ac);
						noRisksChildAc.path = [];
						noRisksChildAc.path.push(`${ac.id}|${ac.accountName}`);
						noRisksChildAc.path.push(`${as.knowledgeAssertionId}`);
						noRisksChildAc.path.push('NO_RISKS_ASSOCIATED');
						accountRisks.push(noRisksChildAc);
					}
				}
			});
		}

		//Sort account risks
		accountRisks.sort((a, b) => {
			return (
				riskTypeDisplayOrder.indexOf(a?.risk?.riskTypeId) - riskTypeDisplayOrder.indexOf(b?.risk?.riskTypeId) ||
				a.risk?.riskName.localeCompare(b.risk?.riskName)
			);
		});

		rawAccounts = [...rawAccounts, ...accountAssertions, ...accountRisks];
	}

	//Sort accounts by name and return
	return _.orderBy(rawAccounts, [(account) => account.accountName.toLowerCase()], ['asc']);
};

export const getAccountsPagingOptions = createSelector([getAccountsSlice], (slice) => ({
	page: slice?.page,
	pages: slice?.pages
}));

export const getAccountsForALRAValidations = (state) => {
	const slice = getAccountsSlice(state);
	let result = [];
	if (slice.allIds.length > 0) {
		let accounts = Object.values(slice?.byId);

		result = accounts.map((account) => ({
			...account,
			relatedDocuments:
				account.relatedDocuments?.filter((doc) => doc.documentTypeID === documentTypes.ALRA_INDIVIDUAL) || []
		}));
	}
	return _.orderBy(result, [(account) => account.accountName.toLowerCase()], ['asc']);
};

const getRiskAssertionFromAssertionRisk = (data) => {
	let risks = {};
	let estimates = {};
	let assertions = [];
	let estimateTypeIds = [estimateTypes.higher, estimateTypes.low, estimateTypes.veryLow, estimateTypes.notSelected];
	data?.map((assertion) => {
		assertion?.risks?.map((risk) => {
			if (estimateTypeIds.includes(risk?.riskTypeId)) {
				if (!estimates[risk?.id]) {
					estimates[risk?.id] = {...risk, assertions: [assertion]};
				} else {
					estimates[risk?.id]?.assertions.push(assertion);
				}
			} else {
				if (!risks[risk?.id]) {
					risks[risk?.id] = {...risk, assertions: [assertion]};
				} else {
					risks[risk?.id]?.assertions.push(assertion);
				}
			}
		});
		assertions.push(assertion);
	});
	return [Object.values(risks), assertions, Object.values(estimates)];
};

export const alphanumericSort = (estA, estB) => {
	if (estA?.estimateName?.toString()?.toLowerCase() === estB?.estimateName?.toString()?.toLowerCase()) {
		if (estA?.estimateName === estB?.estimateName) return 0;
		return estA?.estimateName > estB?.estimateName ? 1 : -1;
	}
	return estA?.estimateName?.toString()?.toLowerCase() > estB?.estimateName?.toString()?.toLowerCase() ? 1 : -1;
};

export const getAccountsForEstimateGridDetails = (slice, doc, labels) => {
	let rawAccounts = [];
	if (slice.allIds.length > 0) {
		let accounts = Object.values(slice?.byId);
		let documents = Object.values(doc?.byId);

		//Iterate accounts to create all parent and child rows
		for (const account of accounts) {
			//Check for limited risk and insignificant risk account types
			if (account.accountTypeId === AccountType.LimitedRisk || account.accountTypeId === AccountType.InSignificant) {
				rawAccounts.push({
					[labels.accountsAndRelatedEstimates]: account?.accountName,
					accountName: account?.accountName,
					executionTypeId: account?.executionTypeId,
					[labels.risk]: [],
					[labels.ALRAAssertions]: account.accountTypeId,
					[labels.estimatesTitle]: 0,
					estimateNames: [],
					parentRow: true,
					hasEstimate: account.hasEstimate
				});
				continue;
			}

			//Retrieve risks and assertions from account
			let [risks, assertions, estimates] = getRiskAssertionFromAssertionRisk(account?.assertions);

			let accountEstimates = [];

			if (estimates?.length > 0) {
				//Create child records for estimates
				estimates?.map((estimate) => {
					let filterDocs = documents.filter((f) => f.smartEvidenceSourceId === estimate?.id);
					let mappedDocuments = [];

					filterDocs.map((doc) => {
						mappedDocuments.push({
							documentName: doc.documentName,
							documentTypeID: doc.documentTypeId,
							fileExt: doc.fileExt,
							id: doc.id,
							riskId: estimate?.id
						});
					});
					accountEstimates.push({
						estimateName: estimate?.riskName,
						[labels.ALRAAssertions]: estimate?.assertions,
						['id']: estimate?.id,
						['documents']: mappedDocuments
						// onClick: () => console.log('clicked ' + estimate?.riskName)
					});
				});
			}
			// Sort estimates by name
			accountEstimates.sort(alphanumericSort);

			rawAccounts.push({
				[labels.accountsAndRelatedEstimates]: account?.accountName,
				accountName: account?.accountName,
				executionTypeId: account?.executionTypeId,
				risks: risks,
				[labels.ALRAAssertions]: assertions,
				estimateCount: estimates?.length,
				estimateNames: accountEstimates,
				parentRow: true,
				hasEstimate: account.hasEstimate
			});
		}
	}
	//Sort accounts by name and return
	return _.orderBy(rawAccounts, [(account) => account?.accountName?.toString()?.toLowerCase()], ['asc']);
};
